'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { Actor, InspectionEvent, Observer, createActor } from 'xstate';
import { useSessionTransition } from '../session';
import { authMachine } from './auth';
import { AuthTransport } from './transport';

export interface AuthContextType {
  authActor: Actor<typeof authMachine>;
  /**
   * The visitor's identity. Held steady for the duration of a session
   * transition — see the `AuthProvider` doc block for the two ordering
   * constraints that hold depends on (it must be mounted inside
   * `SessionTransitionProvider`, and the gate is `transitionRef`, not
   * `isTransitioning`).
   *
   * This is the ONLY session value published here. "Is a session change
   * happening?" belongs to `useSessionTransition()`, not to auth state.
   *
   * There used to be an `isLoggingOut` flag too, derived from
   * `unauthenticating || loggedOut.afterLogout`. It is deliberately gone:
   * `afterLogout` is a TERMINAL state, so the flag stayed true for the life of
   * the actor and wedged everything gated on it — the header account menu became
   * permanently unclickable, the logout overlay reappeared on unrelated pages,
   * and the account-page sign-in redirect stopped firing altogether. Every
   * consumer invented its own escape hatch and none of them held. Removing it
   * outright is what stops that being reintroduced (CTAP-1151).
   */
  isLoggedIn: boolean;
}

/**
 * Auth state is derived ONCE, here in the provider, and published whole.
 *
 * It used to be derived inside `useAuth()`, which meant every one of ~60 call
 * sites ran its own `useState` + `authActor.subscribe`. Beyond the waste, that
 * made the identity freeze below unsound: a component mounting mid-transition
 * seeded `isLoggedIn` from the LIVE snapshot while its already-mounted siblings
 * were still holding the pre-transition value, so two parts of the same screen
 * disagreed about whether the visitor was signed in. One shared value cannot
 * diverge.
 */
const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = (): AuthContextType => useContext(AuthContext);

// export const useUser = () => {
//   return useContext(AuthContext).getSnapshot().context.user;
// };

type AuthOptions = {
  inspect: Observer<InspectionEvent> | undefined;
  endpoints: {
    login: string;
    logout: string;
    refresh: string;
    ssrRefresh: string;
  };
  /** Header injection for the auth HTTP calls — see `./transport`. */
  transport?: AuthTransport;
};

/**
 * runs on server side as part of withAuth HOF
 *
 * @todo should probably update signature to pass Partial<AuthContext> and
 * Options, endpoints should probably be moved into context
 */
export const getAuth = async (
  refreshToken = '',
  options: AuthOptions = {
    inspect: undefined,
    endpoints: { login: '', logout: '', refresh: '', ssrRefresh: '' },
  },
) => {
  return createActor(authMachine, {
    input: {
      transport: options.transport,
      initialContext: {
        endpoints: options.endpoints,
        refreshToken,
      },
    },
    inspect: options.inspect,
  }).start();
};

type AuthSnapshot = ReturnType<Actor<typeof authMachine>['getSnapshot']>;

/**
 * Signed-in predicate — `loggedIn` OR `loggedInReady`.
 *
 * `loggedInReady` is the rehydration state: an actor restored from a serialized
 * snapshot on the server, authenticated but waiting for the client `RESTORE`
 * event. The visitor genuinely IS signed in there, so it must count.
 *
 * It is easy to miss because it is unreachable in a consumer that prepends the
 * `initializing` state — that short-circuits straight to `loggedIn` whenever a
 * token is present, so `loggedInReady` never occurs and omitting it does no
 * visible harm. Consumers that restore a snapshot through the `withAuth` HOF do
 * reach it, and for them omitting it renders an authenticated visitor as signed
 * out for the whole of hydration.
 */
const isSignedIn = (snapshot: AuthSnapshot): boolean =>
  snapshot.matches('loggedIn') || snapshot.matches('loggedInReady');

type Props = {
  actor: Actor<typeof authMachine>;
  children: ReactNode;
};

/**
 * Publishes the auth actor and a single derived `isLoggedIn`.
 *
 * ## Two ordering constraints you can get wrong silently
 *
 * **1. `SessionTransitionProvider` MUST be an ancestor of this provider.**
 * `isLoggedIn` is held steady for the duration of a session transition, and that
 * hold is read from `useSessionTransition()`. Mounted outside the transition
 * provider, this reads the inert default context (`transitionRef: {current:
 * false}`) — which is safe rather than throwing, so nothing warns and nothing
 * breaks in an obvious way. The identity simply flips mid-transition anyway: the
 * overlay covers the screen while the checkout swaps to the guest email form
 * underneath it, and the browser offers autofill for the guest email.
 *
 * **2. The hold is gated on `transitionRef`, not `isTransitioning`** — and any
 * consumer mirroring this identity into its own state must do the same.
 * `isTransitioning` is React state, so it only reflects a transition after a
 * re-render, while actor subscriptions fire synchronously on the machine's own
 * transitions and routinely outrun it: a local sign-out reaches `afterLogout`
 * while `isTransitioning` still reads `false`. If you keep a copy of the
 * identity (feeding a router provider, say), the fix is to **delete the mirror
 * and read the held value here** rather than to reproduce the ref gating.
 *
 * ## Why the identity is held but the actor is not
 *
 * The actor freeze in the app's provider tree covers the quote/checkout
 * subtrees, but the auth actor must stay LIVE — components send LOGIN/LOGOUT
 * through it and several subscribe directly, so a frozen proxy would deadlock
 * sign-in. The identity alone is held; transition progress stays live because the
 * overlay renders from it (CTAP-1150/1151).
 */
export function AuthProvider({ actor, children }: Props) {
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    isSignedIn(actor.getSnapshot()),
  );

  // Hold `isLoggedIn` steady while a session transition is in flight.
  //
  // The actor freeze in `Providers` covers the quote/checkout subtrees, but the
  // auth actor must stay LIVE — components send LOGIN/LOGOUT through it and
  // several subscribe to it directly, so a frozen proxy would deadlock sign-in
  // and latch `useAuthTransitioning`. So the identity alone is held: without it
  // the checkout Information step swaps to the guest email form behind the
  // overlay, which the browser then offers autofill for. The progress signals
  // below stay live by design — the overlay depends on them.
  const { isTransitioning, transitionRef } = useSessionTransition();

  useEffect(() => {
    const subscription = actor.subscribe((snapshot) => {
      // Gate on the REF, not the state value. This callback runs synchronously
      // with the machine's transition, which outruns React: a local sign-out
      // reaches `afterLogout` while `isTransitioning` still reads false, so the
      // identity would flip mid-freeze.
      if (!transitionRef.current) {
        if (isSignedIn(snapshot)) {
          setIsLoggedIn(true);
        } else if (snapshot.matches('loggedOut')) {
          setIsLoggedIn(false);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [actor, transitionRef]);

  // Re-sync when the freeze lifts. The machine usually settles DURING the
  // transition, so there is no later emission to carry the final value — the
  // held update would otherwise be lost and the identity would stay stale until
  // some unrelated auth event happened to fire.
  useEffect(() => {
    if (isTransitioning) {
      return;
    }
    const snapshot = actor.getSnapshot();
    if (isSignedIn(snapshot)) {
      // Freeze-lift re-sync from an external store: the machine settled
      // DURING the transition, so no later emission carries the final value;
      // this effect is the only place the held identity can catch up
      // (CTAP-1150/1151). Hence the set-state-in-effect disables.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoggedIn(true);
    } else if (snapshot.matches('loggedOut')) {
      setIsLoggedIn(false);
    }
  }, [isTransitioning, actor]);

  const value = useMemo(
    () => ({ authActor: actor, isLoggedIn }),
    [actor, isLoggedIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
